/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Manage roles", description = "Control what groups can access and do in each workspace with pre-built or custom roles.", lastUpdatedDate = new Date(1790553600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Manage roles",
        description: "Control what groups can access and do in each workspace with pre-built or custom roles.",
        lastUpdatedDate: "Mon Sep 28 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "How roles work",
          "depth": 0,
          "id": "how-roles-work"
        }, {
          "value": "Choose a role type",
          "depth": 0,
          "id": "choose-a-role-type"
        }, {
          "value": "Pre-built roles",
          "depth": 0,
          "id": "pre-built-roles"
        }, {
          "value": "Pre-built role permissions",
          "depth": 1,
          "id": "pre-built-role-permissions"
        }, {
          "value": "Assign a pre-built role",
          "depth": 1,
          "id": "assign-a-pre-built-role"
        }, {
          "value": "Custom roles",
          "depth": 0,
          "id": "custom-roles"
        }, {
          "value": "Create or edit a custom role",
          "depth": 1,
          "id": "create-or-edit-a-custom-role"
        }, {
          "value": "Custom role grant reference",
          "depth": 0,
          "id": "custom-role-grant-reference"
        }, {
          "value": "General",
          "depth": 1,
          "id": "general"
        }, {
          "value": "Sources",
          "depth": 1,
          "id": "sources"
        }, {
          "value": "Destinations",
          "depth": 1,
          "id": "destinations"
        }, {
          "value": "Models",
          "depth": 1,
          "id": "models"
        }, {
          "value": "Agents",
          "depth": 1,
          "id": "agents"
        }, {
          "value": "AI Decisioning",
          "depth": 1,
          "id": "ai-decisioning"
        }, {
          "value": "Consent API",
          "depth": 1,
          "id": "consent-api"
        }, {
          "value": "Native Delivery",
          "depth": 1,
          "id": "native-delivery"
        }, {
          "value": "Spaces",
          "depth": 1,
          "id": "spaces"
        }, {
          "value": "Events",
          "depth": 1,
          "id": "events"
        }, {
          "value": "Subsets",
          "depth": 1,
          "id": "subsets"
        }, {
          "value": "Approval flows",
          "depth": 1,
          "id": "approval-flows"
        }, {
          "value": "FAQ",
          "depth": 0,
          "id": "faq"
        }, {
          "value": "Can I hide resources from specific users?",
          "depth": 1,
          "id": "can-i-hide-resources-from-specific-users"
        }, {
          "value": "What happens if a user belongs to more than one group?",
          "depth": 1,
          "id": "what-happens-if-a-user-belongs-to-more-than-one-group"
        }, {
          "value": "Who can manage traits on a related or event model?",
          "depth": 1,
          "id": "who-can-manage-traits-on-a-related-or-event-model"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      h2: "h2",
      p: "p",
      ol: "ol",
      li: "li",
      ul: "ul",
      hr: "hr",
      h3: "h3",
      img: "img",
      h4: "h4"
    }, _provideComponents(), props.components), {FeatureFlag, BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Field"
            }), _jsx(_components.th, {
              children: "Content"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Organization admins"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["Organization admin permissions. Workspace admins can change the role of groups that already have access to their workspace; see ", _jsx(_components.a, {
                href: "/workspace-management/user-management#change-a-groups-role-from-workspace-settings",
                children: "Change a group's role from workspace settings"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Roles control what a group can access and do within a workspace. Hightouch uses role-based access control (RBAC) and assigns roles to groups rather than individual users."
      }), "\n", _jsx(_components.p, {
        children: "The access model works like this:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Users"
          }), " belong to one or more groups."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Groups"
          }), " receive access to individual workspaces."]
        }), "\n", _jsxs(_components.li, {
          children: ["Each group is assigned a ", _jsx(_components.strong, {
            children: "role"
          }), " for each workspace it can access."]
        }), "\n", _jsx(_components.li, {
          children: "The role determines what members of that group can access and do in the workspace."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["A role can be ", _jsx(_components.strong, {
          children: "pre-built"
        }), " or ", _jsx(_components.strong, {
          children: "custom"
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Pre-built roles"
          }), " provide a standard set of permissions across a workspace."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Custom roles"
          }), " let you configure permissions for specific resources and products."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "New groups don't have access to any workspaces by default. A user who doesn't belong to a group with workspace access can sign in to Hightouch but can't access that workspace."
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "how-roles-work",
        children: "How roles work"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch's permissions are built around data flow: who can access data and where they can send it. Permissions for sources and destinations determine what users can do with the models and syncs that depend on them."
      }), "\n", _jsx(_components.p, {
        children: "For example, a sync from BigQuery to Salesforce involves:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The ", _jsx(_components.strong, {
            children: "source"
          }), " (BigQuery)"]
        }), "\n", _jsxs(_components.li, {
          children: ["A ", _jsx(_components.strong, {
            children: "model"
          }), " built from that source"]
        }), "\n", _jsxs(_components.li, {
          children: ["The ", _jsx(_components.strong, {
            children: "destination"
          }), " (Salesforce)"]
        }), "\n", _jsxs(_components.li, {
          children: ["The ", _jsx(_components.strong, {
            children: "sync"
          }), " connecting the model to the destination"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Models inherit access from their source, and sync permissions depend on both the source and destination. This lets you control where users can access data and where they can send it without configuring permissions for every model or sync individually."
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "To create or run a sync, users need the required permissions for both the\nsource and destination."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "choose-a-role-type",
        children: "Choose a role type"
      }), "\n", _jsx(_components.p, {
        children: "Every group needs a role before it can access a workspace. Hightouch provides two types of roles:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Role type"
            }), _jsx(_components.th, {
              children: "Use when"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Pre-built"
              })
            }), _jsx(_components.td, {
              children: "A group can use a standard workspace-wide access level: Workspace admin, editor, draft editor, or viewer. Pre-built roles are the fastest way to onboard a team."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Custom"
              })
            }), _jsx(_components.td, {
              children: "A group needs access to specific sources, destinations, models, products, or other resources, or different teams sharing a workspace need distinct permissions."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Start with a pre-built role when it provides the access your group needs. Create a custom role when you need more specific controls, such as:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Restricting a team to specific sources or destinations"
        }), "\n", _jsx(_components.li, {
          children: "Giving marketers access to Customer Studio parent models without source-level permissions"
        }), "\n", _jsx(_components.li, {
          children: "Controlling access to subsets"
        }), "\n", _jsx(_components.li, {
          children: "Separating draft and approval permissions"
        }), "\n", _jsxs(_components.li, {
          children: ["Aligning roles with ", _jsx(_components.a, {
            href: "/customer-studio/destination-rules",
            children: "destination rules"
          })]
        }), "\n", _jsx(_components.li, {
          children: "Giving an Events team access to event resources without full editor permissions"
        }), "\n", _jsx(_components.li, {
          children: "Giving a team access to specific products or resources"
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "pre-built-roles",
        children: "Pre-built roles"
      }), "\n", _jsx(_components.p, {
        children: "Pre-built roles apply a standard set of permissions across all sources, destinations, models, parent models, Agents, and Events resources in a workspace."
      }), "\n", _jsx(_components.p, {
        children: "Hightouch provides four pre-built roles:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Workspace admin"
          }), " — Full access to workspace resources and settings. Can manage credentials, approve changes, and configure the workspace. Organization admins automatically receive this role."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Workspace editor"
          }), " — Can create and edit most resources, create sources and destinations, and trigger syncs. Can't manage credentials or configure the Customer Studio schema."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Workspace draft editor"
          }), " — Can create and edit drafts where supported, view data, and trigger published syncs. Can't approve changes or create sources or destinations."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Workspace viewer"
          }), " — Read-only access. Can't view row-level data or make changes."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "pre-built-role-permissions",
        children: "Pre-built role permissions"
      }), "\n", _jsx(_components.p, {
        children: "The following tables show the grants included with each pre-built role. Workspace viewer has no grants and isn't shown."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "General"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Permission"
            }), _jsx(_components.th, {
              children: "Admin"
            }), _jsx(_components.th, {
              children: "Editor"
            }), _jsx(_components.th, {
              children: "Draft editor"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Create new sources"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "❌"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Create new destinations"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "❌"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Sources"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Permission"
            }), _jsx(_components.th, {
              children: "Admin"
            }), _jsx(_components.th, {
              children: "Editor"
            }), _jsx(_components.th, {
              children: "Draft editor"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "View row-level data"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Draft models & syncs"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Approve models & syncs"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "❌"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Configure schema"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "❌"
            }), _jsx(_components.td, {
              children: "❌"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Manage source"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "❌"
            }), _jsx(_components.td, {
              children: "❌"
            })]
          })]
        })]
      }), "\n", _jsxs(Info, {
        children: [_jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Configure schema"
          }), " controls access to Customer Studio schema configuration, including destination rules and row-level access. It also includes permission to manage traits and trait templates."]
        }), _jsxs(_components.p, {
          children: ["To let users manage traits without configuring the schema, create a ", _jsx(_components.a, {
            href: "#custom-roles",
            children: "custom role"
          }), " with the ", _jsx(_components.a, {
            href: "#parent-models",
            children: _jsx(_components.strong, {
              children: "Manage traits"
            })
          }), " grant."]
        }), _jsxs(_components.p, {
          children: ["Workspaces migrated from Hightouch's earlier permissions model may have a legacy \"Workspace Editor\" custom role that includes ", _jsx(_components.strong, {
            children: "Configure schema"
          }), ". The current pre-built Workspace editor role doesn't."]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Destinations"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Permission"
            }), _jsx(_components.th, {
              children: "Admin"
            }), _jsx(_components.th, {
              children: "Editor"
            }), _jsx(_components.th, {
              children: "Draft editor"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Trigger syncs"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Draft models & syncs"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Approve models & syncs"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "❌"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Manage destination"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "❌"
            }), _jsx(_components.td, {
              children: "❌"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Models — Parent models"
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Customer Studio doesn't use drafts for audiences. Approval flows don't apply\nwhen creating or editing audiences, but may apply to audience syncs."
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Permission"
            }), _jsx(_components.th, {
              children: "Admin"
            }), _jsx(_components.th, {
              children: "Editor"
            }), _jsx(_components.th, {
              children: "Draft editor"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "View parent model data"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Create and edit audiences"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Manage traits"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "❌"
            }), _jsx(_components.td, {
              children: "❌"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Trigger audience syncs"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Approve audience syncs"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "❌"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Configure journeys"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "❌"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Publish journeys"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "❌"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Only workspace admins automatically receive access to ", _jsx(_components.a, {
          href: "#subsets",
          children: "subsets"
        }), ". Editors and draft editors can't create, approve, or sync audiences that use subsets until you grant their group access to those subsets, either in a custom role or from the subset's ", _jsx(_components.strong, {
          children: "Grant access to user groups"
        }), " setting in ", _jsx(_components.strong, {
          children: "Customer Studio"
        }), " > ", _jsx(_components.strong, {
          children: "Governance"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Models — General models"
        })
      }), "\n", _jsx(FeatureFlag, {
        featureName: "General models"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Permission"
            }), _jsx(_components.th, {
              children: "Admin"
            }), _jsx(_components.th, {
              children: "Editor"
            }), _jsx(_components.th, {
              children: "Draft editor"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "View model data"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Agents"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Permission"
            }), _jsx(_components.th, {
              children: "Admin"
            }), _jsx(_components.th, {
              children: "Editor"
            }), _jsx(_components.th, {
              children: "Draft editor"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Access Agents"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Configure Agents"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "❌"
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "AI Decisioning, Consent API, and Native Delivery"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Only workspace admins receive AI Decisioning, Consent API, and Native Delivery permissions by default. Use a ", _jsx(_components.a, {
          href: "#custom-roles",
          children: "custom role"
        }), " to give other groups access."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Events"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Permission"
            }), _jsx(_components.th, {
              children: "Admin"
            }), _jsx(_components.th, {
              children: "Editor"
            }), _jsx(_components.th, {
              children: "Draft editor"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Create new event sources"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "❌"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Create new event warehouse destinations"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "❌"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Create new event streaming destinations"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "❌"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Create new event contracts"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "❌"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Create new event functions"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "❌"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "assign-a-pre-built-role",
        children: "Assign a pre-built role"
      }), "\n", _jsx(_components.p, {
        children: "Roles are assigned to groups rather than individual users."
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Organization settings"
          }), " > ", _jsx(_components.a, {
            href: "https://app.hightouch.com/organization/groups?from=docs",
            children: _jsx(_components.strong, {
              children: "Groups"
            })
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select or create a group."
        }), "\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "Workspaces"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select a pre-built role from the ", _jsx(_components.strong, {
            children: "Role"
          }), " dropdown."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Save changes"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/permissions/group-workspaces.png",
          alt: "The Workspaces tab of a group in Organization settings, with the Role dropdown open"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Access"
        }), " column shows the destinations the group has permissions for in each workspace. Hover over a destination to see the group's grants for it. The column doesn't show every grant or resource type."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/roles/group-access-column.png",
          alt: "The Access column for a group with the Workspace draft editor role, showing its grants for a Meta Custom Audiences destination"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "custom-roles",
        children: "Custom roles"
      }), "\n", _jsx(BtOnly, {
        featureName: "Custom roles"
      }), "\n", _jsx(_components.p, {
        children: "Custom roles let you configure permissions for specific resources and products instead of applying the same access level across the workspace."
      }), "\n", _jsx(_components.p, {
        children: "The custom role builder organizes grants into these tabs:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Tab"
            }), _jsx(_components.th, {
              children: "What it controls"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "General"
              })
            }), _jsx(_components.td, {
              children: "Whether users can create sources or destinations in the workspace."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Sources"
              })
            }), _jsx(_components.td, {
              children: "How users interact with connected data sources, including viewing row-level data, managing credentials, and creating models."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Destinations"
              })
            }), _jsx(_components.td, {
              children: "What users can do with destinations, including creating syncs, triggering sync runs, and managing credentials."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Models"
              })
            }), _jsx(_components.td, {
              children: "Access to parent and general models. Parent model grants control audiences, traits and trait templates, journeys, and AI Decisioning access. General model grants control row-level data access, including for Agents."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Agents"
              })
            }), _jsx(_components.td, {
              children: "Access to Agents, Ad Studio, and Lifecycle Studio, and whether users can modify Agents settings."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "AI Decisioning"
              })
            }), _jsx(_components.td, {
              children: "What users can do in AI Decisioning agents and which parent models those agents can target. Appears only in workspaces with AI Decisioning."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Consent API"
              })
            }), _jsx(_components.td, {
              children: "Whether API keys owned by users in the group can read or update subscription and channel consent. Appears only in workspaces with Native Delivery."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Native Delivery"
              })
            }), _jsx(_components.td, {
              children: "Access to Native Delivery campaigns, messages, assets, channels, and settings. Appears only in workspaces with Native Delivery."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Spaces"
              })
            }), _jsx(_components.td, {
              children: "Whether the group is restricted to specific spaces and which spaces it can access. Appears only in workspaces with spaces enabled."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Who can create and manage Event resources, including sources, destinations, contracts, and functions."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "create-or-edit-a-custom-role",
        children: "Create or edit a custom role"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Organization settings"
          }), " > ", _jsx(_components.a, {
            href: "https://app.hightouch.com/organization/groups?from=docs",
            children: _jsx(_components.strong, {
              children: "Groups"
            })
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Select or create a group."
        }), "\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "Workspaces"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Custom…"
          }), " from the ", _jsx(_components.strong, {
            children: "Role"
          }), " dropdown."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Save changes"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Edit custom role…"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Configure the grants for the role. See the ", _jsx(_components.a, {
            href: "#custom-role-grant-reference",
            children: "custom role grant reference"
          }), " for what each grant allows."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Save changes"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "custom-role-grant-reference",
        children: "Custom role grant reference"
      }), "\n", _jsx(_components.p, {
        children: "Use the following sections to understand each grant available in the custom role builder."
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Jump to:"
        }), " ", _jsx(_components.a, {
          href: "#general",
          children: "General"
        }), " · ", _jsx(_components.a, {
          href: "#sources",
          children: "Sources"
        }), " · ", _jsx(_components.a, {
          href: "#destinations",
          children: "Destinations"
        }), " · ", _jsx(_components.a, {
          href: "#models",
          children: "Models"
        }), " · ", _jsx(_components.a, {
          href: "#agents",
          children: "Agents"
        }), " · ", _jsx(_components.a, {
          href: "#ai-decisioning",
          children: "AI Decisioning"
        }), " · ", _jsx(_components.a, {
          href: "#consent-api",
          children: "Consent API"
        }), " · ", _jsx(_components.a, {
          href: "#native-delivery",
          children: "Native Delivery"
        }), " · ", _jsx(_components.a, {
          href: "#spaces",
          children: "Spaces"
        }), " · ", _jsx(_components.a, {
          href: "#events",
          children: "Events"
        }), " · ", _jsx(_components.a, {
          href: "#subsets",
          children: "Subsets"
        }), " · ", _jsx(_components.a, {
          href: "#approval-flows",
          children: "Approval flows"
        })]
      }), "\n", _jsx(_components.h3, {
        id: "general",
        children: "General"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/roles/custom-role-general.png",
          alt: "The General tab of the custom role builder, with toggles to create new sources and destinations"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Grant"
            }), _jsx(_components.th, {
              children: "What it allows"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Create new sources"
              })
            }), _jsx(_components.td, {
              children: "Create data sources in the workspace and assign access permissions for other groups."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Create new destinations"
              })
            }), _jsx(_components.td, {
              children: "Create destinations in the workspace and assign access permissions for other groups."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Grant support access"
              })
            }), _jsx(_components.td, {
              children: "Grant Hightouch support access to the workspace. Appears only when your organization's support access settings require explicit approval. Workspace admins have this grant by default."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["In the role builder, these appear as the ", _jsx(_components.strong, {
          children: "Can this role create new sources?"
        }), ", ", _jsx(_components.strong, {
          children: "Can this role create new destinations?"
        }), ", and ", _jsx(_components.strong, {
          children: "Can this role grant support access to Hightouch?"
        }), " toggles."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Only workspace admins can configure workspace-level resources such as folders,\nextensions, custom storage, and API keys."
        })
      }), "\n", _jsx(_components.h3, {
        id: "sources",
        children: "Sources"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/roles/custom-role-sources.png",
          alt: "The Sources tab of the custom role builder, with per-source grant checkboxes"
        })
      }), "\n", _jsxs(Info, {
        children: [_jsx(_components.p, {
          children: "Source grants are primarily intended for Reverse ETL use cases where users need to work directly with data from a source, including running custom SQL."
        }), _jsxs(_components.p, {
          children: ["For marketers working in Customer Studio, avoid enabling source grants. Use ", _jsx(_components.a, {
            href: "#parent-models",
            children: "parent model grants"
          }), " instead, which limit access to specific parent models and subsets so marketers work within your pre-approved Customer Studio schema."]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "View source data"
        })
      }), "\n", _jsx(_components.p, {
        children: "Lets users view row-level data from the source across Hightouch, including:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Previewing models"
        }), "\n", _jsx(_components.li, {
          children: "Viewing row-level data when testing rows during sync creation"
        }), "\n", _jsx(_components.li, {
          children: "Accessing row-level data in the debugger for historical sync runs"
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Without ", _jsx(_components.strong, {
            children: "View source data"
          }), ", users can still see each sync run's status and\nrun-level errors, but not the row-level ", _jsx(_components.strong, {
            children: "Successful"
          }), " and ", _jsx(_components.strong, {
            children: "Rejected"
          }), " tabs\nin the run details, which show each row's primary key and error."]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Configure models & syncs"
        })
      }), "\n", _jsx(_components.p, {
        children: "Lets users:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Create, edit, or delete models that use the source"
        }), "\n", _jsx(_components.li, {
          children: "Use those models in syncs when they also have the required destination permissions"
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Users can edit an existing model only if they can also edit all syncs that\ndepend on it."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["When ", _jsx(_components.a, {
          href: "#approval-flows",
          children: "approval flows"
        }), " are enabled, this grant splits into ", _jsx(_components.strong, {
          children: "Draft models & syncs"
        }), " and ", _jsx(_components.strong, {
          children: "Approve models & syncs"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Configure schema"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Available only in workspaces with Customer Studio. Lets users configure ", _jsx(_components.a, {
          href: "/customer-studio/schema",
          children: "schema"
        }), " resources, including parent models, traits, sync templates, destination rules, and row-level access."]
      }), "\n", _jsxs(_components.p, {
        children: ["This grant also lets users manage traits and trait templates. To let users manage traits without configuring the schema, use ", _jsx(_components.a, {
          href: "#parent-models",
          children: _jsx(_components.strong, {
            children: "Manage traits"
          })
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Manage source"
        })
      }), "\n", _jsx(_components.p, {
        children: "Lets users manage the source's configuration, credentials, and permissions, including:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Connection details and credentials"
        }), "\n", _jsx(_components.li, {
          children: "Sync engine"
        }), "\n", _jsx(_components.li, {
          children: "Source-level Warehouse Sync Logs defaults"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "destinations",
        children: "Destinations"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/roles/custom-role-destinations.png",
          alt: "The Destinations tab of the custom role builder, with per-destination grant checkboxes"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Grant"
            }), _jsx(_components.th, {
              children: "What it allows"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Trigger syncs"
              })
            }), _jsx(_components.td, {
              children: "Manually trigger or cancel sync runs, and turn syncs on or off. Doesn't allow users to edit schedules."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Configure models & syncs"
              })
            }), _jsx(_components.td, {
              children: "Create, edit, or delete syncs to the destination and configure schedules. Doesn't allow users to manually trigger runs."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Manage destination"
              })
            }), _jsx(_components.td, {
              children: "Manage the destination's configuration, connection details, credentials, and permissions."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["When ", _jsx(_components.a, {
          href: "#approval-flows",
          children: "approval flows"
        }), " are enabled, ", _jsx(_components.strong, {
          children: "Configure models & syncs"
        }), " splits into ", _jsx(_components.strong, {
          children: "Draft models & syncs"
        }), " and ", _jsx(_components.strong, {
          children: "Approve models & syncs"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "models",
        children: "Models"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Models"
        }), " tab controls permissions for parent models and general models."]
      }), "\n", _jsx(_components.h4, {
        id: "parent-models",
        children: "Parent models"
      }), "\n", _jsxs(Info, {
        children: ["The ", _jsx(_components.strong, {
          children: "Models"
        }), " tab was previously named ", _jsx(_components.strong, {
          children: "Customer Studio"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/roles/custom-role-parent-models.png",
          alt: "The Parent models sub-tab of the custom role builder, with grants for all parent models and a Subsets section for an individual parent model"
        })
      }), "\n", _jsx(_components.p, {
        children: "Parent model grants work like source grants, but are scoped to specific parent models in the Customer Studio schema."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "View parent model data"
        })
      }), "\n", _jsx(_components.p, {
        children: "Lets users view row-level data from the parent model across Hightouch, including:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Previewing individual audience members"
        }), "\n", _jsx(_components.li, {
          children: "Viewing row-level data when testing rows during sync creation"
        }), "\n", _jsx(_components.li, {
          children: "Accessing row-level data in the debugger for historical sync runs"
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Users with ", _jsx(_components.strong, {
            children: "Configure audiences & syncs"
          }), " but without ", _jsx(_components.strong, {
            children: "View parent model\ndata"
          }), " can still see audience size counts, but not audience insights or\nrow-level data for individual members."]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Configure audiences & syncs"
        })
      }), "\n", _jsx(_components.p, {
        children: "Lets users:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Create, edit, or delete audiences that use the parent model"
        }), "\n", _jsx(_components.li, {
          children: "Use those audiences in syncs when they also have the required destination permissions"
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Users can edit an existing audience only if they can also edit all syncs that\ndepend on it."
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Manage traits"
        })
      }), "\n", _jsx(_components.p, {
        children: "Lets users create, edit, and delete traits and trait templates associated with the selected parent model, including traits on its related and event models."
      }), "\n", _jsx(_components.p, {
        children: "You can grant this permission across all parent models in the workspace or for individual parent models."
      }), "\n", _jsxs(_components.p, {
        children: ["Users with ", _jsx(_components.strong, {
          children: "Configure schema"
        }), " on the underlying source can manage traits and trait templates regardless of the ", _jsx(_components.strong, {
          children: "Manage traits"
        }), " setting."]
      }), "\n", _jsxs(_components.p, {
        children: ["If a related or event model is connected to multiple parent models, a user only needs ", _jsx(_components.strong, {
          children: "Manage traits"
        }), " for one of those parent models. See ", _jsx(_components.a, {
          href: "#who-can-manage-traits-on-a-related-or-event-model",
          children: "Who can manage traits on a related or event model?"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Configure journeys"
        })
      }), "\n", _jsx(_components.p, {
        children: "Lets users create, edit, or delete journeys and journey templates that use the parent model, and configure sync tiles when they also have the required destination permissions. Journey templates that aren't tied to a parent model require a workspace admin."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Publish journeys"
        })
      }), "\n", _jsx(_components.p, {
        children: "Lets users launch, pause, or stop live journeys."
      }), "\n", _jsxs(_components.p, {
        children: ["Once a journey is live, users with either ", _jsx(_components.strong, {
          children: "Configure journeys"
        }), " or ", _jsx(_components.strong, {
          children: "Publish journeys"
        }), " can pause or resume it. Pausing or resuming a journey that sends Native Delivery messages also requires ", _jsx(_components.strong, {
          children: "Manage messages"
        }), " on the ", _jsx(_components.a, {
          href: "#native-delivery",
          children: "Native Delivery"
        }), " tab."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Configure journeys"
        }), " and ", _jsx(_components.strong, {
          children: "Publish journeys"
        }), " appear only in workspaces with journeys enabled."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Work with AI Decisioning agents"
        })
      }), "\n", _jsx(_components.p, {
        children: "Available only in workspaces with AI Decisioning. Lets users create and operate AI Decisioning agents that target the parent model."
      }), "\n", _jsxs(_components.p, {
        children: ["This is the same setting as ", _jsx(_components.strong, {
          children: "Agent access by parent model"
        }), " on the ", _jsx(_components.a, {
          href: "#ai-decisioning",
          children: "AI Decisioning"
        }), " tab, so changing it in one place changes it in the other. Users also need the workspace-level grants on that tab."]
      }), "\n", _jsx(_components.h4, {
        id: "general-models",
        children: "General models"
      }), "\n", _jsx(FeatureFlag, {
        featureName: "General models"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "General models"
        }), " sub-tab appears only in workspaces with Agents."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/roles/custom-role-general-models.png",
          alt: "The General models sub-tab of the custom role builder, with the View model data grant"
        })
      }), "\n", _jsx(_components.p, {
        children: "General model grants apply per model, similar to source grants. You can configure grants for all general models in the workspace, including models created later, or for individual models."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Grant"
            }), _jsx(_components.th, {
              children: "What it allows"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "View model data"
              })
            }), _jsx(_components.td, {
              children: "Access row-level data from the general model. Required for Agents to query the model on the user's behalf."
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["Creating and managing general models requires ", _jsx(_components.strong, {
          children: "Configure schema"
        }), " on the underlying ", _jsx(_components.a, {
          href: "#sources",
          children: "source"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "agents",
        children: "Agents"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/roles/custom-role-agents.png",
          alt: "The Agents tab of the custom role builder, with toggles to access and configure Agents"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Grant"
            }), _jsx(_components.th, {
              children: "What it allows"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Access Agents"
              })
            }), _jsx(_components.td, {
              children: "Access Agents, Ad Studio, and Lifecycle Studio. Without this grant, these sections are hidden."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Configure Agents"
              })
            }), _jsx(_components.td, {
              children: "Modify Agents settings, including context, brand, guardrails, and channels. Without this grant, Context Hub is hidden."
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["In the role builder, these appear as the ", _jsx(_components.strong, {
          children: "Can this role access Agents?"
        }), " and ", _jsx(_components.strong, {
          children: "Can this role configure Agents?"
        }), " toggles."]
      }), "\n", _jsxs(_components.p, {
        children: ["Agents also respects model-level and row-level data controls. Which tables an agent can query depends on the user's permissions for ", _jsx(_components.a, {
          href: "#parent-models",
          children: "parent models"
        }), " and ", _jsx(_components.a, {
          href: "#general-models",
          children: "general models"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["For row-level and column-level access controls, see ", _jsx(_components.a, {
          href: "/agents/overview#data-governance",
          children: "Agents data governance"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "ai-decisioning",
        children: "AI Decisioning"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "AI Decisioning"
        }), " tab appears only in workspaces with ", _jsx(_components.a, {
          href: "/ai-decisioning/overview",
          children: "AI Decisioning"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Grant"
            }), _jsx(_components.th, {
              children: "What it allows"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Edit content"
              })
            }), _jsx(_components.td, {
              children: "Edit message content, variables and variants, tags, collections, and content suggestions, and send preview messages."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Manage agents"
              })
            }), _jsxs(_components.td, {
              children: ["Everything included in ", _jsx(_components.strong, {
                children: "Edit content"
              }), ", plus agent configuration, channels, outcomes, scheduling, offers, and agent lifecycle."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/roles/custom-role-ai-decisioning.png",
          alt: "The AI Decisioning tab of the custom role builder, with the Edit content and Manage agents grants and agent access by parent model"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Under ", _jsx(_components.strong, {
          children: "Agent access by parent model"
        }), ", choose whether these grants apply to ", _jsx(_components.strong, {
          children: "All parent models"
        }), ", including parent models created later, or ", _jsx(_components.strong, {
          children: "Specific parent models"
        }), ". This setting is the same as the ", _jsx(_components.strong, {
          children: "Work with AI Decisioning agents"
        }), " grant on the ", _jsx(_components.a, {
          href: "#parent-models",
          children: "Parent models"
        }), " sub-tab."]
      }), "\n", _jsx(_components.p, {
        children: "Agents that target other parent models remain visible but read-only."
      }), "\n", _jsx(_components.h3, {
        id: "consent-api",
        children: "Consent API"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Consent API"
        }), " tab appears only in workspaces with Native Delivery. These grants apply to API keys owned by users in the group, not to actions users take in the Hightouch app."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/roles/custom-role-consent-api.png",
          alt: "The Consent API tab of the custom role builder, with toggles to read and update consent"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Grant"
            }), _jsx(_components.th, {
              children: "What it allows"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Can this role read consent via the Consent API?"
              })
            }), _jsx(_components.td, {
              children: "Read any user's current topic subscriptions and channel consent state."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Can this role update consent via the Consent API?"
              })
            }), _jsx(_components.td, {
              children: "Write topic subscriptions and channel-level consent for any user."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "native-delivery",
        children: "Native Delivery"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Native Delivery"
        }), " tab appears only in workspaces with Native Delivery. Without ", _jsx(_components.strong, {
          children: "View Native Delivery"
        }), ", the Native Delivery section doesn't appear in the sidebar."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/roles/custom-role-native-delivery.png",
          alt: "The Native Delivery tab of the custom role builder, with toggles for each Native Delivery grant"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Grant"
            }), _jsx(_components.th, {
              children: "What it allows"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "View Native Delivery"
              })
            }), _jsx(_components.td, {
              children: "See campaigns, messages, assets, and dashboards."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Draft messages & campaigns"
              })
            }), _jsx(_components.td, {
              children: "Create and edit messages, campaigns, and message tiles in journeys."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Manage messages"
              })
            }), _jsx(_components.td, {
              children: "Launch, schedule, pause, and cancel campaigns and journeys with message tiles."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Manage assets"
              })
            }), _jsx(_components.td, {
              children: "Publish and delete assets."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Manage channels"
              })
            }), _jsx(_components.td, {
              children: "Configure email, SMS, and push channels."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Manage settings"
              })
            }), _jsx(_components.td, {
              children: "Configure providers, domains, brand, consent, and suppression."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "spaces",
        children: "Spaces"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Spaces"
        }), " tab appears only in workspaces with spaces enabled, and only workspace admins can see it."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Does this role only have space access?"
          }), " — When enabled, users with this role can only access the spaces they're assigned to, and can't see resources outside those spaces."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Space access"
          }), " — Lists the spaces assigned to the role."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "events",
        children: "Events"
      }), "\n", _jsx(FeatureFlag, {
        featureName: "Events permissions"
      }), "\n", _jsx(_components.p, {
        children: "Events permissions have two levels:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Creation grants"
          }), " are workspace-level and control who can create new Event resources."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Per-resource grants"
          }), " control what users can do with specific Event resources."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Events"
        }), " tab appears only in workspaces with Events or real-time audiences, and only workspace admins can see it."]
      }), "\n", _jsx(_components.h4, {
        id: "creation-grants",
        children: "Creation grants"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "General"
        }), " sub-tab under ", _jsx(_components.strong, {
          children: "Events"
        }), " controls which Event resources users can create. Each grant also lets the creator assign access permissions for that resource to other groups."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/roles/custom-role-events-general.png",
          alt: "The General sub-tab of the Events tab in the custom role builder, with toggles for each creation grant"
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Grant"
            }), _jsx(_components.th, {
              children: "What it allows"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Create new event sources"
              })
            }), _jsx(_components.td, {
              children: "Create event sources and grant other groups permission to manage them."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Create new event warehouse destinations"
              })
            }), _jsx(_components.td, {
              children: "Create event warehouse destinations and grant other groups permission to manage them."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Create new event streaming destinations"
              })
            }), _jsx(_components.td, {
              children: "Create event streaming destinations and grant other groups permission to manage them."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Create new event contracts"
              })
            }), _jsx(_components.td, {
              children: "Create event contracts for defining event schemas and data quality rules."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Create new event functions"
              })
            }), _jsx(_components.td, {
              children: "Create event transformation functions."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "per-resource-grants",
        children: "Per-resource grants"
      }), "\n", _jsxs(_components.p, {
        children: ["The remaining ", _jsx(_components.strong, {
          children: "Events"
        }), " sub-tabs control what users can do with individual Event resources. Available grants depend on the resource type:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Resource type"
            }), _jsx(_components.th, {
              children: "Can edit"
            }), _jsx(_components.th, {
              children: "Can sync"
            }), _jsx(_components.th, {
              children: "Can access data"
            }), _jsx(_components.th, {
              children: "Can run"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Event sources"
              })
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "—"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Streaming destinations"
              })
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "—"
            }), _jsx(_components.td, {
              children: "—"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Warehouse destinations"
              })
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "—"
            }), _jsx(_components.td, {
              children: "✅"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Functions"
              })
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "—"
            }), _jsx(_components.td, {
              children: "—"
            }), _jsx(_components.td, {
              children: "—"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Contracts"
              })
            }), _jsx(_components.td, {
              children: "✅"
            }), _jsx(_components.td, {
              children: "—"
            }), _jsx(_components.td, {
              children: "—"
            }), _jsx(_components.td, {
              children: "—"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Can edit"
          }), " — Modify the resource's configuration."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Can sync"
          }), " — Create and manage syncs involving the resource."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Can access data"
          }), " — View live event data in the source's debugger and event inspector. Available only for event sources."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Can run"
          }), " — Trigger runs. Available only for warehouse destinations."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Each sub-tab lists the resources of that type, plus an ", _jsx(_components.strong, {
          children: "All [resource type] in this workspace"
        }), " row that also covers resources created later."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/roles/custom-role-events-sources.png",
          alt: "The Sources sub-tab of the Events tab in the custom role builder, with Can edit, Can sync, and Can access data checkboxes for each event source"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/roles/custom-role-events-streaming-destinations.png",
          alt: "The Streaming Destinations sub-tab of the Events tab in the custom role builder, with Can edit and Can sync checkboxes for each streaming destination"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/roles/custom-role-events-warehouse-destinations.png",
          alt: "The Warehouse Destinations sub-tab of the Events tab in the custom role builder, with Can edit, Can sync, and Can run checkboxes for each event warehouse destination"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/roles/custom-role-events-functions.png",
          alt: "The Functions sub-tab of the Events tab in the custom role builder, with a Can edit checkbox for event functions"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/roles/custom-role-events-contracts.png",
          alt: "The Contracts sub-tab of the Events tab in the custom role builder, with a Can edit checkbox for event contracts"
        })
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "To create or manage an event sync, users need the required permissions for\nboth the event source and event destination."
        })
      }), "\n", _jsxs(Info, {
        children: [_jsx(_components.p, {
          children: "Workspace admins and groups with the pre-built Workspace editor role automatically receive all Events creation grants. Other groups can't create Event resources until you configure the creation grants."
        }), _jsxs(_components.p, {
          children: ["Workspace admins also receive every per-resource grant. Workspace editors receive every per-resource grant except ", _jsx(_components.strong, {
            children: "Can edit"
          }), ": ", _jsx(_components.strong, {
            children: "Can sync"
          }), " and ", _jsx(_components.strong, {
            children: "Can access data"
          }), " on event sources, ", _jsx(_components.strong, {
            children: "Can sync"
          }), " and ", _jsx(_components.strong, {
            children: "Can run"
          }), " on warehouse destinations, and ", _jsx(_components.strong, {
            children: "Can sync"
          }), " on streaming destinations."]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "subsets",
        children: "Subsets"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/customer-studio/subsets",
          children: "Subsets"
        }), " divide a parent model into permissioned segments, such as by brand, region, or consent type."]
      }), "\n", _jsxs(_components.p, {
        children: ["Configure subsets in ", _jsx(_components.strong, {
          children: "Customer Studio"
        }), " > ", _jsx(_components.strong, {
          children: "Governance"
        }), " > ", _jsx(_components.strong, {
          children: "Subset categories"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "For example:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["An ", _jsx(_components.strong, {
            children: "EU marketing team"
          }), " can build audiences using only EU data."]
        }), "\n", _jsxs(_components.li, {
          children: ["An ", _jsx(_components.strong, {
            children: "email marketing team"
          }), " can use only consented audiences."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["When subsets are enabled, the role builder provides additional controls for applying grants such as ", _jsx(_components.strong, {
          children: "View parent model data"
        }), " and ", _jsx(_components.strong, {
          children: "Configure audiences & syncs"
        }), " to specific subset values."]
      }), "\n", _jsx(_components.p, {
        children: "How these controls work depends on whether the subset category is required."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/roles/roles-required-subsets.png",
          alt: "Subset required toggle"
        })
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Required subset category"
          }), " — Users can configure and use audiences only within the selected subset values. If you don't select any values, they can't configure or use any audiences."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Optional subset category"
          }), " — Users can configure and use audiences with the selected subset values or without a subset."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "approval-flows",
        children: "Approval flows"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.a, {
          href: "/workspace-management/approval-flows",
          children: "Approval flows"
        }), " add a review step before publishing model or sync changes."]
      }), "\n", _jsxs(_components.p, {
        children: ["Enable approval flows in ", _jsx(_components.strong, {
          children: "Workspace settings"
        }), " > ", _jsx(_components.strong, {
          children: "Workspace"
        }), " under ", _jsx(_components.strong, {
          children: "Require approvals"
        }), " > ", _jsx(_components.strong, {
          children: "Syncs & Models"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/workspace-management/permissions/require-approvals-toggle.png",
          alt: "Require approvals in Workspace settings, with the Syncs & Models toggle turned on"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["When approval flows are enabled, ", _jsx(_components.strong, {
          children: "Configure models & syncs"
        }), " on the ", _jsx(_components.strong, {
          children: "Sources"
        }), " and ", _jsx(_components.strong, {
          children: "Destinations"
        }), " tabs splits into two grants:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Draft models & syncs"
          }), " — Create or edit drafts. Users can't delete published resources."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Approve models & syncs"
          }), " — Review and approve or deny drafts."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Together, these grants provide the same permissions as ", _jsx(_components.strong, {
          children: "Configure models & syncs"
        }), " when approval flows aren't enabled."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Approval flows don't apply when creating or editing audiences in Customer\nStudio because audiences don't have drafts. Approval may still be required to\nsync an audience to a destination."
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Draft models & syncs"
          }), " and ", _jsx(_components.strong, {
            children: "Approve models & syncs"
          }), " are separate grants.\nTo let users publish their own changes without another user's approval, assign\nboth grants. The user creates a draft and then immediately self-approves it."]
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["To approve a sync, users need ", _jsx(_components.strong, {
            children: "Approve models & syncs"
          }), " for both the source\nand destination, granted through the same role."]
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "faq",
        children: "FAQ"
      }), "\n", _jsx(_components.h3, {
        id: "can-i-hide-resources-from-specific-users",
        children: "Can I hide resources from specific users?"
      }), "\n", _jsx(_components.p, {
        children: "Only in specific cases. Roles control what users can do with resources, not whether they can see them. By default, every member of a workspace can see its sources, destinations, models, and syncs, including their configuration."
      }), "\n", _jsx(_components.p, {
        children: "Roles limit visibility in these cases:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Row-level data"
          }), " — Users without ", _jsx(_components.a, {
            href: "#sources",
            children: _jsx(_components.strong, {
              children: "View source data"
            })
          }), " or ", _jsx(_components.a, {
            href: "#parent-models",
            children: _jsx(_components.strong, {
              children: "View parent model data"
            })
          }), " can see a resource but not the customer rows in it."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Product sections"
          }), " — Groups without the relevant grants don't see Agents, Ad Studio, Lifecycle Studio, and Context Hub (", _jsx(_components.a, {
            href: "#agents",
            children: "Agents"
          }), "), or Native Delivery (", _jsx(_components.a, {
            href: "#native-delivery",
            children: "Native Delivery"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Spaces"
          }), " — In workspaces with spaces enabled, a role restricted to its assigned spaces can't see resources outside those spaces (", _jsx(_components.a, {
            href: "#spaces",
            children: "Spaces"
          }), ")."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "To hide other resources from some users, place those resources in a separate workspace that those users' groups can't access."
      }), "\n", _jsx(_components.h3, {
        id: "what-happens-if-a-user-belongs-to-more-than-one-group",
        children: "What happens if a user belongs to more than one group?"
      }), "\n", _jsx(_components.p, {
        children: "Users can belong to multiple groups in the same workspace. They receive the combined permissions of all their group memberships."
      }), "\n", _jsx(_components.p, {
        children: "Permissions from different groups don't combine to create new source-to-destination paths. For example, suppose:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Group 1 can sync from source A to destination B."
        }), "\n", _jsx(_components.li, {
          children: "Group 2 can sync from source C to destination D."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "A user in both groups can sync from A to B and from C to D. They can't sync from A to D or from C to B based only on those memberships."
      }), "\n", _jsx(_components.h3, {
        id: "who-can-manage-traits-on-a-related-or-event-model",
        children: "Who can manage traits on a related or event model?"
      }), "\n", _jsx(_components.p, {
        children: "Traits and trait templates can belong to parent, related, or event models. The required permission depends on the associated parent model:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Parent model"
          }), " — Users need ", _jsx(_components.strong, {
            children: "Manage traits"
          }), " for that parent model."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Related or event model"
          }), " — Users need ", _jsx(_components.strong, {
            children: "Manage traits"
          }), " for a parent model connected directly or indirectly to that model. If the model is connected to multiple parent models, permission for any one of them is sufficient."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Users with ", _jsx(_components.strong, {
          children: "Configure schema"
        }), " on the underlying source can manage traits and trait templates regardless of their ", _jsx(_components.strong, {
          children: "Manage traits"
        }), " grants. This includes workspace admins, who receive ", _jsx(_components.strong, {
          children: "Configure schema"
        }), " by default."]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsx(_components.p, {
        children: "Roles control who can take actions in a workspace, but they don't enforce organization-wide policies like consent requirements or PII handling."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/workspace-management/user-management",
            children: "Manage users and groups"
          }), " to add users to the groups you assign roles to."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/workspace-management/approval-flows",
            children: "Configure approval flows"
          }), " to require review before draft editors publish model and sync changes."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/subsets",
            children: "Configure subsets"
          }), " to limit which customer rows a role can build audiences from."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/customer-studio/destination-rules",
            children: "Configure destination rules"
          }), " to control what data can be sent to each destination, regardless of role."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/workspace-management/environments",
            children: "Set up environments"
          }), " to test syncs in a staging workspace before granting production access."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
